import { useState, useEffect } from "react";
import axios from "axios";
import { Star, ArrowUpRight, PenLine } from "lucide-react";
import { Reveal, Label } from "./motion";
import { TRAINER } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLACE_ID = "ChIJC8p_6IX3t4kRg7Cwl7ZWYWg";
const WRITE_REVIEW_URL = `https://search.google.com/local/writereview?placeid=${PLACE_ID}`;

export const Reviews = () => {
  const [rating, setRating] = useState(5.0);
  const [count, setCount] = useState(7);
  const [mapsUrl, setMapsUrl] = useState(TRAINER.googleUrl);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(({ data }) => {
      if (data.configured) {
        if (data.rating) setRating(data.rating);
        if (data.user_rating_count != null) setCount(data.user_rating_count);
        if (data.google_maps_uri) setMapsUrl(data.google_maps_uri);
        if (data.reviews?.length) setReviews(data.reviews);
      }
    }).catch(() => {});
  }, []);

  return (
    <section id="reviews" className="px-4 md:px-8 lg:px-12 py-24 md:py-32 lg:py-48" data-testid="reviews-section">
      <Reveal><Label>04 — Reviews</Label></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display text-5xl sm:text-6xl lg:text-8xl leading-none mt-6 text-white max-w-4xl">
          Rated <span className="text-[#FF3B30]">{Number(rating).toFixed(1)}</span> on Google
        </h2>
      </Reveal>

      <div className="mt-16 grid lg:grid-cols-12 gap-6 items-stretch">
        {/* Big rating panel */}
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="h-full border border-zinc-800 bg-[#121212] p-8 lg:p-10 flex flex-col justify-between" data-testid="rating-panel">
            <div className="flex items-end gap-5">
              <div className="font-display text-7xl lg:text-8xl text-white leading-[0.8]">{Number(rating).toFixed(1)}</div>
              <div className="pb-2">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={20} className="fill-[#FF3B30] text-[#FF3B30]" />
                  ))}
                </div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mt-2" data-testid="review-count">
                  Based on {count} Google reviews
                </div>
              </div>
            </div>
            <p className="text-zinc-400 mt-8 leading-relaxed">
              Real, verified reviews from Jurado Performance clients across Annapolis. Every rating reflects the same standard: accountability, technique and results that last.
            </p>
          </div>
        </Reveal>

        {/* Actions */}
        <Reveal delay={0.25} className="lg:col-span-7">
          <div className="h-full border border-zinc-800 bg-[#0A0A0A] p-8 lg:p-10 flex flex-col justify-between gap-8">
            <div>
              <h3 className="font-display text-3xl lg:text-4xl text-white leading-none">See what clients say — and add your own</h3>
              <p className="text-zinc-400 mt-4 leading-relaxed max-w-lg">
                Read every review on our Google Business page, or if we&apos;ve trained together, leave a quick review to help others start their journey.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="google-reviews-link"
                 className="flex-1 group flex items-center justify-between gap-4 bg-[#FF3B30] text-white px-6 py-4 font-bold uppercase tracking-wider hover:bg-[#D6281F] transition-colors">
                Read on Google
                <ArrowUpRight size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
              <a href={WRITE_REVIEW_URL} target="_blank" rel="noreferrer" data-testid="write-review-link"
                 className="flex-1 group flex items-center justify-between gap-4 border border-zinc-700 text-white px-6 py-4 font-bold uppercase tracking-wider hover:bg-zinc-900 transition-colors">
                Leave a Review
                <PenLine size={18} className="text-[#FF3B30]" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Live review quote cards — only render if Google returns any */}
      {reviews.length > 0 && (
        <div className="grid md:grid-cols-3 gap-6 mt-6">
          {reviews.slice(0, 6).map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 0.1}>
              <div className="h-full p-8 border border-zinc-800 bg-[#121212] flex flex-col" data-testid={`review-card-${i}`}>
                <div className="flex gap-1 mb-5">
                  {[...Array(Math.round(r.rating || 5))].map((_, j) => (
                    <Star key={j} size={14} className="fill-[#FF3B30] text-[#FF3B30]" />
                  ))}
                </div>
                <p className="text-zinc-300 leading-relaxed flex-1">{`"${r.text || "Rated 5 stars."}"`}</p>
                <div className="mt-6 pt-5 border-t border-zinc-800 flex items-center gap-3">
                  {r.profile_photo_url ? (
                    <img src={r.profile_photo_url} alt={r.author_name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 bg-[#FF3B30] flex items-center justify-center font-display text-white">
                      {r.author_name?.[0] || "G"}
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-semibold text-white">{r.author_name}</div>
                    <div className="text-xs text-zinc-500">{r.relative_time_description}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
};
